import { z } from 'zod';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { MASTER_LIFECYCLE_STATUSES, archivableFieldsSchema, showLifecycleSchema } from './lifecycle.schema';

/**
 * The vehicle type master (PA_REQUIREMENTS FR-5.1.4's "vehicle applicability").
 *
 * ⚠️ THIS IS DATA, NOT A TypeScript ENUM AND NOT A UNION TYPE.
 *
 * There is deliberately no `VEHICLE_TYPES = [...] as const` anywhere in this package.
 * `PLACE_SOURCES` and `RANK_CATEGORIES` sit nearby and make a tuple look like the house
 * style; they are closed vocabularies the code branches on. This is an admin-editable
 * list that WILL change — more types, retired types — and every retired type must keep
 * rendering in the published policy versions that reference it. A union type would make
 * each of those a deploy.
 *
 * ⚠️ AND `ANY` IS A FLAGGED WILDCARD ROW, NOT A NAME. See `isWildcard` below.
 */
export const vehicleTypeBaseSchema = z.object({
  name: z.string().trim().min(1, 'Vehicle type name is required').max(80),
  /**
   * The label the department actually uses — "PCRV", "QRT". Unlike `PostType.code`,
   * which is an internal upsert key, this one is shown, so it is on the DTO.
   */
  code: z
    .string()
    .trim()
    .min(1, 'Code is required')
    .max(40)
    .transform((value) => value.toUpperCase()),
  displayOrder: z.number().int().min(0).max(10_000),
});

export const createVehicleTypeSchema = vehicleTypeBaseSchema.extend({
  displayOrder: vehicleTypeBaseSchema.shape.displayOrder.default(0),
});

export const updateVehicleTypeSchema = vehicleTypeBaseSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, { message: 'At least one field must be provided' });

/**
 * ⚠️ `isWildcard` IS ABSENT FROM EVERY INPUT SCHEMA, DELIBERATELY.
 *
 * It is set by the seeder and by nothing else. The model carries a partial unique index
 * on `{ is_wildcard: true }` that permits exactly ONE wildcard row, so letting an
 * administrator move the flag turns that constraint into an opaque E11000 in a dialog
 * they cannot act on. `api/tests/integration/vehicleType.test.ts` asserts the absence.
 */
export const vehicleTypeSchema = vehicleTypeBaseSchema.extend({
  id: objectIdSchema,
  /**
   * ⚠️ THE MATCHING FLAG. When plate-level vehicle assignment arrives, a requirement
   * line for the wildcard must be satisfiable by a vehicle of ANY type — and the
   * matching logic reads THIS. It must never compare against `code === 'ANY'` and never
   * against `name`.
   *
   * The seeded wildcard is coded `ANY` but NAMED "Any vehicle", precisely so that a
   * name-comparing implementation fails on the seed data rather than in production.
   * `services/vehicleType.service.ts` exposes `getWildcardVehicleType()` so there is one
   * place that knows how to find it.
   */
  isWildcard: z.boolean(),
  /** Archive, never delete — see `postType.schema.ts`. Retiring a type is this. */
  ...archivableFieldsSchema.shape,
  /** `PUBLISHED` when this row has ever been part of a published policy version. */
  archivedFromStatus: z.enum(MASTER_LIFECYCLE_STATUSES).nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** Embedded in a policy version so the requirement grid needs no second round trip. */
export const vehicleTypeSummarySchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  code: z.string(),
  isWildcard: z.boolean(),
  /**
   * ⚠️ [FIX-1] THE FACT A CONSUMER NEEDS TO STOP OFFERING AN ARCHIVED ROW.
   *
   * A published policy version stores its columns FOR EVER (P2 froze them deliberately —
   * an order raised last month must still render exactly as it was issued), so the array
   * is the RECORD and never changes when a master row is archived. What has to change is
   * what the record is USED for, and that decision cannot be made without knowing whether
   * the row is still in circulation. Carrying it on the summary is what lets ONE
   * resolution function answer it — see `web/src/lib/policyColumns.ts`.
   *
   * ⚠️ [LIFECYCLE] THIS WAS `isActive`, AND IT IS THE SAME FACT UNDER ONE VOCABULARY.
   */
  archivedAt: z.iso.datetime().nullable(),
});

export const VEHICLE_TYPE_SORT_FIELDS = ['displayOrder', 'name'] as const;
export type VehicleTypeSortField = (typeof VEHICLE_TYPE_SORT_FIELDS)[number];

export const listVehicleTypesQuerySchema = createPaginationSchema(VEHICLE_TYPE_SORT_FIELDS, 'displayOrder')
  .extend({
    search: z.string().trim().min(1).max(80).optional(),
    /** ⚠️ [LIFECYCLE] Replaced `includeInactive`, and it is not a rename — see `postType.schema.ts`. */
    show: showLifecycleSchema,
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });
